import '../styles.scss'

import PropTypes from 'prop-types'
import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { useActions, useValues } from 'kea'

import preloadImages from 'lib/utils/preload-images'
import railsContext from 'lib/selectors/rails-context'
import centerZoomFromLocations, { hasLatitudeLongitude, hasRadius } from 'lib/utils/geo/center-zoom-from-locations'

import Map from '~/scenes/shared/map'
import Marker from '~/scenes/shared/map/marker'
import Circle from '~/scenes/shared/map/circle'

import courseShowLogic from '~/scenes/course/show/logic'

const MapPigeon = ({ onLoaded }) => {
  const { setHoverLocation } = useActions(courseShowLogic)
  const {
    course: { id, locations },
    hoverLocation
  } = useValues(courseShowLogic)
  const { dpr } = useSelector(railsContext)
  const [center, setCenter] = useState(centerZoomFromLocations(locations).center)
  const [zoom, setZoom] = useState(centerZoomFromLocations(locations).zoom)
  const mapRef = useRef(null)
  let objects = []

  const setMapLocation = location => {
    const { center, zoom } = centerZoomFromLocations([location])
    setCenter(center)
    setZoom(zoom)
  }

  const _preloadImages = useCallback(() => {
    const finalDpr = typeof window !== 'undefined' ? window.devicePixelRatio || dpr : dpr
    const dprExtension = finalDpr >= 2 ? '@2x' : ''

    const images = [
      `/images/map/pin-green-large${dprExtension}.png`,
      `/images/map/pin-green-large-hover${dprExtension}.png`,
      `/images/map/pin-orange-large${dprExtension}.png`,
      `/images/map/pin-orange-large-hover${dprExtension}.png`
    ]

    preloadImages(images)
  }, [dpr])

  useEffect(() => {
    onLoaded && onLoaded(setMapLocation)
    const rect = mapRef.current.getBoundingClientRect()
    const { center, zoom } = centerZoomFromLocations(locations, rect.width, rect.height, 20)
    setCenter(center)
    setZoom(zoom)
    _preloadImages()
  }, [_preloadImages, locations, onLoaded])

  const handleMouseOver = location => {
    setHoverLocation(location.id)
  }

  const handleMouseOut = () => {
    setHoverLocation(null)
  }

  const onBoundsChanged = ({ center, zoom }) => {
    setCenter(center)
    setZoom(zoom)
  }

  const _setZoom = zoom => {
    setZoom(zoom)
  }

  locations.filter(hasLatitudeLongitude).forEach(location => {
    objects.push(
      <Marker
        key={`location-${location.id}`}
        resultId={id}
        anchor={[location.latitude, location.longitude]}
        offset={[0, 0]}
        lat={location.latitude}
        lng={location.longitude}
        image={location.type === 'in_house' ? 'orange' : 'green'}
        location={location}
        onMouseOver={handleMouseOver}
        onMouseOut={handleMouseOut}
        hover={location.id === hoverLocation}
        dpr={dpr}
      />
    )
  })

  locations.filter(hasRadius).forEach(location => {
    objects.push(
      <Circle
        key={`circle-${location.id}`}
        anchor={[location.latitude, location.longitude]}
        offset={[0, 0]}
        lat={location.latitude}
        lng={location.longitude}
        radius={location.radius}
        hide={location.id !== hoverLocation}
        zIndex={9}
      />
    )
  })

  return (
    <div className='show-course-map-container' ref={mapRef}>
      <Map
        dpr={dpr}
        center={center}
        zoom={zoom}
        onBoundsChanged={onBoundsChanged}
        defaultWidth={564}
        height={300}
        objects={objects}
        setZoom={_setZoom}
        scrollProtect
        childPropsUpdateTrigger={['hover', 'hide']}
      />
    </div>
  )
}

MapPigeon.propTypes = {
  onLoaded: PropTypes.func
}

export default MapPigeon
